import React, { useState, useEffect } from 'react';
import "./Messages.css";
import { FormGroup, Input } from 'reactstrap';
import Inbox from "../../assets/images/inbox.svg";
import Sent from "../../assets/images/sent.svg";
import Draft from "../../assets/images/drafts.svg";
import ComposeMessage from "./ComposeMessage";
import { decryptData } from '../../utils/helper';
import { useDispatch, useSelector } from 'react-redux';
import { deleteMessages, getUserMessages, updateMessage } from '../../api/User';
import Loader from '../../components/Loader/Loader';
import moment from 'moment';
import CustomPagination from '../../components/Pagination/CustomPagination';
import { failed, success } from '../../utils/Tostify';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import ViewMessage from './ViewMessage';
import { useLocation } from "react-router";
import { ITEMS_PER_PAGE } from '../../utils/constants';

const Messages = () => {
    const location = useLocation();
    const [composeModal, setComposeModal] = useState(location.state?.openCompose || false);
    const [messages, setMessages] = useState([]);
    const [selectedMessageIds, setSelectedMessageIds] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [searchKey, setSearchKey] = useState("");
    const [folder, setFolder] = useState("Inbox");
    const [selectedDraft, setSelectedDraft] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectAll, setSelectAll] = useState(false);
    const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
    const [selectedPdfUrl, setSelectedPdfUrl] = useState('');
    const [pdfFileName, setPdfFileName] = useState('');
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState('');
    const dispatch = useDispatch();
    let token = useSelector(state => state.userReducer.token);
    let user = useSelector(state => state.userReducer.user);
    const { getConfirmation } = useConfirmDialog();

    const openPdfModal = (pdfUrl, fileName) => {
        setSelectedPdfUrl(pdfUrl);
        setPdfFileName(fileName);
        setIsPdfModalOpen(true);
    };

    const closePdfModal = () => {
        setIsPdfModalOpen(false);
        setSelectedPdfUrl('');
        setPdfFileName('');
    };

    const openImageModal = (imageUrl) => {
        setSelectedImageUrl(imageUrl);
        setIsImageModalOpen(true);
    };

    const closeImageModal = () => {
        setIsImageModalOpen(false);
        setSelectedImageUrl('');
    };

    const toggleComposeModal = () => {
        setComposeModal(!composeModal);
    };

    const fetchMessages = async (folder = "Inbox", searchKey = "") => {
        setIsLoading(true);
        let res;
        try {
            const values = { folder, searchKey, ...(folder === "Inbox" ? { userId: user._id } : { userId: user._id }) };
            res = await getUserMessages({ values, dispatch, token });
            if (res?.status === "SUCCESS") {
                const decryptedMessages = decryptData(res.data);
                setMessages(decryptedMessages);
                setFolder(folder);
            } else {
                setIsLoading(false);
                failed(res?.message);
            }
        } catch (error) {
            setIsLoading(false);
            failed(res?.message);
        } finally {
            setIsLoading(false);
        }
    };

    const filterMessages = () => {
        if (!searchKey.trim()) return messages;

        return messages.filter((message) =>
            message?.subject?.toLowerCase().includes(searchKey.toLowerCase()) ||
            message?.content?.toLowerCase().includes(searchKey.toLowerCase())
        );
    };

    const searchedMessages = filterMessages();
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedMessages = searchedMessages.slice(startIndex, endIndex);

    useEffect(() => {
        setCurrentPage(1); // Reset to page 1 when search term changes
    }, [searchKey, messages]);

    const handlePagination = (page) => {
        setCurrentPage(page);
    };

    const handleSelectAll = () => {
        let updatedSelectedIds = [...selectedMessageIds];

        if (selectAll) {
            updatedSelectedIds = updatedSelectedIds.filter(id =>
                !paginatedMessages.some(message => message._id === id)
            );
        } else {
            const currentPageIds = paginatedMessages.map(message => message._id);
            updatedSelectedIds = [...new Set([...updatedSelectedIds, ...currentPageIds])];
        }

        setSelectedMessageIds(updatedSelectedIds);
        setSelectAll(!selectAll);

    };

    const handleCheckboxChange = (messageId) => {
        let updatedSelectedIds = [...selectedMessageIds];

        if (updatedSelectedIds.includes(messageId)) {
            updatedSelectedIds = updatedSelectedIds.filter(id => id !== messageId);
        } else {
            updatedSelectedIds.push(messageId);
        }

        setSelectedMessageIds(updatedSelectedIds);
    };

    useEffect(() => {
        const allSelected = paginatedMessages.length > 0 &&
            paginatedMessages.every(message => selectedMessageIds.includes(message._id));

        setSelectAll(allSelected);
    }, [selectedMessageIds, paginatedMessages]);

    useEffect(() => {
        fetchMessages(folder);
    }, [folder]);

    const handleMessageClick = async (message) => {
        setSelectedMessage(message); // Show the message details page

        if (folder === "Sent" || folder === "Draft") {
            return;
        }

        if (!message.isRead) {
            try {
                await updateMessage({ messageId: message._id, data: { isRead: true }, dispatch, token });

                setMessages((prevMessages) =>
                    prevMessages.map((msg) =>
                        msg._id === message._id ? { ...msg, isRead: true } : msg
                    )
                );

                // Ensure the UI updates immediately
                setSelectedMessage((prev) => ({ ...prev, isRead: true }));

            } catch (error) {
                // console.error("Error updating message:", error);
            }
        }
    };

    const handleDraftClick = (draft) => {
        setSelectedDraft(draft);
        setComposeModal(true); // Open Compose modal with selected draft
    };

    const handleDeleteMessages = async () => {
        if (selectedMessageIds.length === 0) return;

        const confirmed = await getConfirmation({
            title: "Attention!",
            actionBtnTitle: "Yes",
            message: `Are you sure you want to delete ${selectedMessageIds.length} messages?`
        });

        if (confirmed) {
            try {
                setIsLoading(true);

                const res = await deleteMessages({
                    values: { selectedMessageIds },
                    dispatch,
                    token
                });

                if (res?.status === "SUCCESS") {
                    setSelectedMessageIds([]);
                    success(res?.message);
                    await fetchMessages(folder);
                } else {
                    setIsLoading(false);
                    failed(res?.message);
                }
            } catch (error) {
                setIsLoading(false);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <>
            {/* table format */}
            <div className='message_container table_padding_blk'>
                {isLoading && <Loader />}
                <div className='d-flex align-items-center flex-wrap'>
                    <div className='messge_blk'>
                        <h2 className="home-title">Messages</h2>
                    </div>
                    <div className='d-flex justify-content-between align-item-center main_header gap-3 flex-wrap'>
                        <div className="d-flex gap-2">
                        <button className='compose_block' onClick={() => { toggleComposeModal(); setSelectedDraft(null); }}>
                            <i className="fa fa-edit"></i> Compose
                        </button>
                        {selectedMessageIds.length > 0 && (
                            <button
                                className="compose_block delete-btn "
                                onClick={handleDeleteMessages}
                            >
                                Delete Messages
                            </button>
                        )}
                        </div>
                        <FormGroup className="spacing_three">
                            <Input
                                id="exampleSearch"
                                name="search"
                                placeholder="Search"
                                type="search"
                                value={searchKey}
                                onChange={(e) => {
                                    setSearchKey(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </FormGroup>
                    </div>
                </div>

                <div className='d-flex flex-wrap'>
                    <div className='sidebar_block'>
                        <div className="tabs_button">
                            <button className={`cancel_btn_blk d-flex gap-3 align-items-center ${folder === "Inbox" ? "active" : ""}`} onClick={() => { setFolder("Inbox"); setSelectedMessage(null); }}>
                                <img src={Inbox} alt="Inbox" />Inbox
                            </button>
                            <button className={`cancel_btn_blk d-flex gap-3 align-items-center ${folder === "Sent" ? "active" : ""}`} onClick={() => { setFolder("Sent"); setSelectedMessage(null); }}>
                                <img src={Sent} alt="Sent" />Sent
                            </button>
                            <button className={`cancel_btn_blk d-flex gap-3 align-items-center ${folder === "Drafts" ? "active" : ""}`} onClick={() => { setFolder("Drafts"); setSelectedMessage(null); }}>
                                <img src={Draft} alt="Draft" />Drafts
                            </button>
                        </div>
                    </div>

                    <div className='content_block px-0 pt-0 profile_main_two'>
                        {!selectedMessage ? (
                            <>
                                {paginatedMessages?.length > 0 && (
                                      <div className='table-wrapper'>
                                    <table className='scroll-table-two table table-hover mb-0' style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                        <thead>
                                            <tr style={{ background: "#f8f9fa" }}>
                                                <th>
                                                    <FormGroup check className="custom-checkbox">
                                                        <Input
                                                            type="checkbox"
                                                            id="select-all"
                                                            checked={selectAll}
                                                            onChange={handleSelectAll}
                                                        />
                                                    </FormGroup>
                                                </th>
                                                <th><span>Subject</span></th>
                                                <th><span>To Team Member</span></th>
                                                {folder === "Inbox" && <th><span>From</span></th>}
                                                {(folder === "Sent" || folder === "Drafts") && localStorage.getItem("userRole") !== "Platform" && localStorage.getItem("userRole") !== "Patient" && <th><span>To Patient</span></th>}
                                                <th><span>Content</span></th>
                                                <th><span>Attachment</span></th>
                                                <th><span>Time</span></th>
                                            </tr>
                                        </thead>
                                        <tbody className='scrollable-tbody-two'>
                                            {paginatedMessages?.map((message) => (
                                                <tr
                                                    key={message._id}
                                                    onClick={() => {
                                                        if (folder === "Drafts") {
                                                            handleDraftClick(message);
                                                        } else {
                                                            handleMessageClick(message);
                                                        }
                                                    }}
                                                    style={{ cursor: "pointer" }}
                                                >
                                                    <td>
                                                        <FormGroup check className="custom-checkbox">
                                                            <Input
                                                                type="checkbox"
                                                                id={`checkbox-${message._id}`}
                                                                checked={selectedMessageIds.includes(message._id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={() => handleCheckboxChange(message._id)}
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td className='text-truncate' style={{ color: (folder === "Sent" || folder === "Drafts") ? "gray" : (message.isRead ? "gray" : "black") }}>
                                                        {message.subject}
                                                    </td>
                                                    <td>{message?.to ? `${message?.to?.firstName} ${message?.to?.lastName}`.trim() : "N/A"}</td>
                                                    {folder === "Inbox" && <td>{message?.senderType !== "Patient" ? message?.senderId?.firstName.trim() + " "+  message?.senderId?.lastName.trim() : message?.senderId?.name[0]?.text && decryptData(message?.senderId?.name[0]?.text)}</td>}
                                                    {/* {(folder === "Sent" || folder === "Drafts") && <td>{message?.patient?.name?.[0]?.text ? decryptData(message.patient.name[0].text) : "N/A"}</td>} */}
                                                    {(folder === "Sent" || folder === "Drafts") && localStorage.getItem("userRole") !== "Platform" && localStorage.getItem("userRole") !== "Patient" && (
                                                    <td>{message?.patient?.name?.[0]?.text ? decryptData(message.patient.name[0].text) : "N/A"}</td>
                                                    )}
                                                    <td className='text-truncate'>{message.content}</td>
                                                    <td>{message.attachments?.length > 0 && <i className="fa fa-paperclip" aria-hidden="true"></i>}</td>
                                                    <td>
                                                        {moment(message.updatedAt).isSame(moment(), 'day') ? (
                                                            moment(message.updatedAt).format("HH:mm")
                                                        ) : moment(message.updatedAt).isSame(moment(), 'month') ? (
                                                            moment(message.updatedAt).format("DD MMM")
                                                        ) : (
                                                            moment(message.updatedAt).format("DD/MM/YYYY")
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                )}
                                <div className="d-flex justify-content-end mt-2">
                                    <CustomPagination
                                        totalPages={Math.ceil(searchedMessages.length / ITEMS_PER_PAGE)}
                                        totalRows={searchedMessages.length}
                                        rowsPerPage={ITEMS_PER_PAGE}
                                        page={currentPage}
                                        handlePage={handlePagination}
                                    />
                                </div>
                            </>
                        ) : (
                            <ViewMessage
                                selectedMessage={selectedMessage}
                                openImageModal={openImageModal}
                                openPdfModal={openPdfModal}
                                setSelectedMessage={setSelectedMessage}
                            />
                        )}
                    </div>
                </div>
                {composeModal && (
                    <ComposeMessage
                        fetchMessages={fetchMessages}
                        isOpen={composeModal}
                        folder={folder}
                        toggle={toggleComposeModal}
                        draftMessage={selectedDraft}
                    />
                )}
            </div>

            {/* Image Modal */}
            <Modal isOpen={isImageModalOpen} toggle={closeImageModal} size="lg">
                <ModalHeader toggle={closeImageModal}>Image Preview</ModalHeader>
                <ModalBody>
                <img src={selectedImageUrl} alt="Selected Image" style={{ width: '100%', height: 'auto' }} />
                </ModalBody>
            </Modal>

            <Modal isOpen={isPdfModalOpen} toggle={closePdfModal} size="lg">
                <ModalHeader toggle={closePdfModal}>
                {pdfFileName}
                </ModalHeader>
                <ModalBody>
                <iframe src={selectedPdfUrl} width="100%" height="600px" title="PDF Preview" />
                </ModalBody>
            </Modal>
        </>
    );
};

export default Messages;
